/**
 * @file use-vertical-split.tsx
 * @description Manages the split percentage and drag handlers for a two-pane, vertically stacked
 * layout inside a canvas view (mirrors the ad-hoc horizontal split logic already duplicated across
 * the canvas views, but for a row-resize divider instead of a column-resize one).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * @param storageKey - localStorage key used to persist the split across sessions. Pass a unique key
 * per divider so unrelated canvas views don't clobber each other's saved split.
 * @param defaultPercent - Initial height share (0-100) of the top/left pane before the user drags.
 * @param minPercent - Smallest height share (0-100) the top/left pane may be resized to.
 * @param maxPercent - Largest height share (0-100) the top/left pane may be resized to.
 */
export function useVerticalSplit(
  storageKey: string,
  defaultPercent: number = 50,
  minPercent: number = 20,
  maxPercent: number = 80,
) {
  const [topPercent, setTopPercent] = useState(() => {
    if (typeof window === 'undefined') return defaultPercent;
    const stored = parseFloat(localStorage.getItem(storageKey) ?? '');
    return Number.isFinite(stored) ? Math.max(minPercent, Math.min(maxPercent, stored)) : defaultPercent;
  });

  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef(0);
  const startPercentRef = useRef(topPercent);
  const rafRef = useRef<number | null>(null);

  const startResizing = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!containerRef.current) return;

      setIsResizing(true);
      startYRef.current = e.clientY;
      startPercentRef.current = topPercent;

      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
    },
    [topPercent],
  );

  useEffect(() => {
    if (!isResizing) return;

    const handlePointerMove = (e: PointerEvent) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      rafRef.current = requestAnimationFrame(() => {
        if (!containerRef.current) return;
        const containerHeightPx = containerRef.current.offsetHeight;
        if (!containerHeightPx) return;
        const deltaPercent = ((e.clientY - startYRef.current) / containerHeightPx) * 100;
        setTopPercent(Math.max(minPercent, Math.min(maxPercent, startPercentRef.current + deltaPercent)));
      });
    };

    const handlePointerUp = () => {
      setIsResizing(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      setTopPercent(current => {
        localStorage.setItem(storageKey, current.toString());
        return current;
      });

      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isResizing, minPercent, maxPercent, storageKey]);

  return { topPercent, isResizing, containerRef, startResizing };
}
